import { FC, ReactNode, useContext, useMemo } from 'react'

// others
import { EntryType } from '../../../generated'
import { AppContext } from '../../context'

/**
 * Gives everything beneath it the entry-type context (`isExpenseEntry`, `isLegalFee`, ...) of
 * `entryType`, without touching the page-level flags.
 *
 * The entry forms and listings read those flags from AppContext, where `/entries/:type` sets them
 * for the whole page. A screen that shows several entry types at once (the matter Timekeeping tab)
 * cannot do that — the flags would be a single global answer to a per-entry question — so it scopes
 * them per subtree instead. The override lives only in this subtree: nothing is dispatched.
 */
export const EntryTypeScope: FC<{ entryType?: EntryType | string | null; children: ReactNode }> = ({
  entryType,
  children
}) => {
  const parent = useContext(AppContext)

  const value = useMemo(
    () => ({
      ...parent,
      isTimeEntry: entryType === EntryType.Time,
      isExpenseEntry: entryType === EntryType.Expense,
      isCostEntry: entryType === EntryType.Cost,
      isLegalFee: entryType === EntryType.Legal,
      isNonClientEntry: entryType === EntryType.NonClient,
      entryType: (entryType as EntryType) || parent.entryType
    }),
    [parent, entryType]
  )

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>
}
